// ==============================
// Type Definitions
// ==============================

interface TargetCostData {
  costs: {
    totalPrices: number;
    finalPWDD: number;
  };
  ranges: {
    lessThanRange: { percentage: number; sharePercentage: number };
    dynamicRanges: { startPercentage: number; endPercentage: number; sharePercentage: number }[];
    greaterThanRange: { percentage: number; sharePercentage: number };
  };
}

interface TargetCostMetrics {
  painGainAmount: number;
  totalContractorShare: number;
  contractorMargin: number;
  costVariance: number;
  rangeCalculations: RangeCalculation[];
}

interface RangeDefinition {
  from: number;
  to: number;
  share: number;
}

type RangeType = 'less-than' | 'greater-than' | 'dynamic';

interface RangeCalculation {
  type: RangeType;
  startPercentage: number;
  endPercentage: number;
  sharePercentage: number;
  amountInRange: number;
  contractorShare: number;
}

// ==============================
// Core Calculations
// ==============================

function calculateAmountInRange(
  targetPrice: number,
  params: {
    fromPercentage: number;
    toPercentage: number;
    actualCostPercentage: number;
    difference: number;
    isOverrun: boolean;
  }
): number {
  // Determine effective range boundaries based on actual cost position
  const effectiveFrom = params.isOverrun 
    ? Math.max(params.fromPercentage, 100)
    : Math.max(params.fromPercentage, params.actualCostPercentage);
    
  const effectiveTo = params.isOverrun
    ? Math.min(params.toPercentage, params.actualCostPercentage)
    : Math.min(params.toPercentage, 100);

  // Validate range applicability
  if (effectiveTo <= effectiveFrom || effectiveTo === effectiveFrom) return 0;
  
  // Calculate exact percentage within range
  const rangePercentage = effectiveTo - effectiveFrom;
  
  // Calculate amount with proper scaling
  const amountInRange = (rangePercentage / 100) * targetPrice;

  // Return precise amount for the range
  return Number(Math.abs(amountInRange).toFixed(2));
}

function calculateTargetCostMetrics(data: TargetCostData): TargetCostMetrics {
  const { costs, ranges } = data;
  const defaultMetrics = {
    painGainAmount: 0,
    totalContractorShare: 0,
    contractorMargin: 0,
    costVariance: 0,
    rangeCalculations: []
  };

  // Validate required data
  if (!costs?.totalPrices || !costs?.finalPWDD || costs.totalPrices <= 0) {
    return defaultMetrics;
  }

  // Calculate key metrics
  const actualCostPercentage = (costs.finalPWDD / costs.totalPrices) * 100;
  // Pain/Gain Amount should be positive when we have savings (finalPWDD < totalPrices)
  // and negative when we have overrun (finalPWDD > totalPrices)
  const painGainAmount = costs.totalPrices - costs.finalPWDD;
  const totalDifference = Math.abs(painGainAmount);
  const isOverrun = painGainAmount < 0; // Changed to match new painGainAmount calculation
  // For savings (painGainAmount < 0), share should be positive
  // For overruns (painGainAmount > 0), share should be negative
  const shareSign = isOverrun ? -1 : 1;

  // Process dynamic ranges
  const dynamicRanges = [...ranges.dynamicRanges]
    .sort((a, b) => a.startPercentage - b.startPercentage)
    .map((range, index, arr) => {
      // Ensure sequential ranges without gaps
      if (index > 0) {
        range.startPercentage = arr[index - 1].endPercentage;
      }
      return range;
    });

  // Calculate dynamic range allocations
  const rangeCalculations = dynamicRanges.map(range => {
    const amountInRange = calculateAmountInRange(costs.totalPrices, {
      fromPercentage: range.startPercentage,
      toPercentage: range.endPercentage,
      actualCostPercentage,
      difference: totalDifference,
      isOverrun
    });

    const contractorShare = amountInRange * (range.sharePercentage / 100);

    return {
      type: 'dynamic' as RangeType,
      startPercentage: range.startPercentage,
      endPercentage: range.endPercentage,
      sharePercentage: range.sharePercentage,
      amountInRange,
      contractorShare: shareSign * contractorShare
    };
  });

  // Calculate boundary ranges
  const calculateBoundaryRange = (
    type: RangeType,
    fromPercentage: number,
    toPercentage: number,
    sharePercentage: number
  ): RangeCalculation | null => {
    const amountInRange = calculateAmountInRange(costs.totalPrices, {
      fromPercentage,
      toPercentage,
      actualCostPercentage,
      difference: totalDifference,
      isOverrun
    });

    if (amountInRange === 0) return null;

    const contractorShare = amountInRange * (sharePercentage / 100);

    return {
      type,
      startPercentage: fromPercentage,
      endPercentage: toPercentage,
      sharePercentage,
      amountInRange,
      contractorShare: shareSign * contractorShare
    };
  };

  // Calculate less-than range
  const lessThanCalc = calculateBoundaryRange(
    'less-than',
    0,
    ranges.lessThanRange.percentage,
    ranges.lessThanRange.sharePercentage
  );

  // Calculate greater-than range
  const greaterThanCalc = calculateBoundaryRange(
    'greater-than',
    ranges.greaterThanRange.percentage,
    Infinity,
    ranges.greaterThanRange.sharePercentage
  );

  // Combine all calculations
  const allCalculations = [
    ...(lessThanCalc ? [lessThanCalc] : []),
    ...rangeCalculations,
    ...(greaterThanCalc ? [greaterThanCalc] : [])
  ];

  // Calculate total contractor share
  const totalContractorShare = allCalculations.reduce(
    (sum, calc) => sum + calc.contractorShare,
    0
  );

  // Calculate cost variance percentage
  const costVariance = (painGainAmount / costs.totalPrices) * 100;
  
  // Calculate contractor margin
  const contractorMargin = costs.finalPWDD !== 0
    ? (totalContractorShare / costs.finalPWDD) * 100
    : 0;

  return {
    painGainAmount,
    totalContractorShare,
    contractorMargin: Math.min(contractorMargin, 100),
    costVariance,
    rangeCalculations: allCalculations
  };
}

// Export types and functions
export type {
  TargetCostData,
  TargetCostMetrics,
  RangeDefinition,
  RangeType,
  RangeCalculation,
};

/**
 * Processes and validates ranges while preventing overlaps
 */
function updateRangeCalculations({
  targetPrice,
  actualCostPct,
  ranges
}: {
  targetPrice: number;
  actualCostPct: number;
  ranges: RangeDefinition[];
}): RangeCalculation[] {
  // Validate inputs
  if (!targetPrice || targetPrice <= 0 || actualCostPct < 0 || !ranges?.length) {
    return [];
  }

  // Clone and sort ranges to prevent input mutation
  const sortedRanges = [...ranges].sort((a, b) => a.from - b.from);

  // Ensure sequential non-overlapping ranges
  const adjustedRanges = sortedRanges.map((range, index, arr) => {
    if (index === 0) return range;
    const prevTo = arr[index - 1].to;
    return { 
      ...range, 
      from: prevTo,
      to: Math.max(range.to, prevTo) // Ensure ranges meet exactly
    };
  });

  const isOverrun = actualCostPct > 100;
  const costDifference = targetPrice * ((actualCostPct - 100) / 100);
  const totalDifference = Math.abs(costDifference);

  // Determine share sign based on whether it's an overrun or saving
  const shareSign = actualCostPct > 100 ? -1 : 1;

  return adjustedRanges.map(range => {
    const amountInRange = calculateAmountInRange(targetPrice, {
      fromPercentage: range.from,
      toPercentage: range.to,
      actualCostPercentage: actualCostPct,
      difference: totalDifference,
      isOverrun
    });

    const contractorShare = amountInRange * (range.share / 100);

    return {
      type: 'dynamic',
      startPercentage: range.from,
      endPercentage: range.to,
      sharePercentage: range.share,
      amountInRange,
      contractorShare: shareSign * contractorShare
    };
  });
}

export {
  calculateAmountInRange,
  calculateTargetCostMetrics,
  updateRangeCalculations,
};